import { useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  DatePicker,
  message,
  Tag,
  Typography,
  Space,
  Modal,
  Input,
  Select,
} from 'antd';
import {
  FilePdfOutlined,
  FileExcelOutlined,
  DeleteOutlined,
  PrinterOutlined,
  FilterOutlined,
  ReloadOutlined,
  IssuesCloseOutlined,
} from '@ant-design/icons';
import { DocumentTypes } from '../../../Helpers/Constants';
import CustomTable from '../../ui/CustomTable';
import billingService from '../../../services/billing.service';
import { createDate, formatDate } from '../../../Helpers/time-utils';
import moment from 'moment';
import print from 'print-js';
import {
  getTicketToBlobUrl,
  downloadFromData,
} from '../../../Helpers/Functions';
import { formatLocalDate } from '../../Modification/Helpers/time';
import InvoiceCreate from './InvoiceCreate';
import SelectAjax from '../../ui/SelectAjax';
const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
let hideMessage = null;

const exportInvoicesDbf = async (filters) => {
  try {
    const data = await billingService.exportDbf(filters);
    downloadFromData(data, 'application/octet-stream', 'CAN26.DBF');
  } catch (error) {
    message.error('Ocurrió un error al exportar el DBF.');
  }
};
const exportInvoicesExcel = async (filters) => {
  try {
    if (!filters?.from || !filters?.to) {
      message.warning('Selecciona un rango de fechas para exportar.');
      return;
    }

    const data = await billingService.exportExcel(filters);

    downloadFromData(
      data,
      'application/vnd.ms-excel',
      `facturacion-${new Date().toISOString().split('T')[0]}.xlsx`,
    );
  } catch (error) {
    if (error.response?.status === 403) {
      message.error('No tienes permisos para exportar este rango de fechas.');
    } else if (error.response?.status === 422) {
      message.error(
        error.response?.data?.message ||
          'Debes seleccionar un rango de fechas para exportar.',
      );
    } else {
      message.error('Ocurrió un error al exportar el reporte.');
    }
  }
};

const exportInvoicesConcar = async (filters) => {
  try {
    const data = await billingService.exportConcar(filters);
    downloadFromData(
      data,
      'application/vnd.ms-excel',
      `concar-${new Date().toISOString().split('T')[0]}.xlsx`,
    );
  } catch (error) {
    if (error.response?.status === 403) {
      message.error('Solo el superadmin puede exportar este reporte.');
    } else {
      message.error('Ocurrió un error al exportar CONCAR.');
    }
  }
};

const Invoices = () => {
  const [open, setOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [url, setUrl] = useState('/billing/invoices');
  const [filterVisible, setFilterVisible] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [form] = Form.useForm();

  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [paymentForm] = Form.useForm();

  const cancellInvoice = async (id) => {
    setIsLoading(true);
    try {
      await billingService.cancellInvoice(id);
      setUrl((url) => url + '?nc=' + Math.random() * 100);
    } catch (error) {
      message.error(
        error.response?.data.message ||
          'Ocurrió un error al anular la factura.',
      );
    } finally {
      setIsLoading(false);
    }
  };
  const openPaymentModal = (record) => {
    setSelectedBilling(record);

    paymentForm.setFieldsValue({
      payment_date: createDate(),
      voucher: record.voucher || '',
    });

    setPaymentModalVisible(true);
  };

  const closePaymentModal = () => {
    setPaymentModalVisible(false);
    setSelectedBilling(null);
    paymentForm.resetFields();
  };

  const markAsPaid = async () => {
    try {
      const values = await paymentForm.validateFields();

      await billingService.markAsPaid(selectedBilling.id, {
        payment_date: formatDate(values.payment_date, 'YYYY-MM-DD'),
        voucher: values.voucher,
      });

      message.success('Factura marcada como pagada.');
      closePaymentModal();

      setUrl((url) => url + '?nc=' + Math.random() * 100);
    } catch (error) {
      if (error.errorFields) return;

      message.error(
        error.response?.data?.message ||
          'Ocurrió un error al registrar el pago.',
      );
    }
  };
  const toggleLoadingRecord = (status) => {
    if (status) {
      hideMessage = message.loading('Descargando...', 0);
    } else if (hideMessage) {
      hideMessage();
      hideMessage = null;
    }
  };

  const printInvoice = async (record) => {
    toggleLoadingRecord(true);
    const url = await getTicketToBlobUrl(record.external_invoice_id);
    print(url);
    toggleLoadingRecord(false);
  };

  const invoiceTypeMap = {
    '01': { label: 'Factura', color: 'blue' },
    '03': { label: 'Boleta', color: 'cyan' },
    '07': { label: 'Nota Créd.', color: 'purple' },
  };

  const columns = [
    {
      title: 'Comprobante',
      key: 'invoice_type',
      sorter: true,
      render: (text, record) => {
        const typeInfo = invoiceTypeMap[record.invoice_type] || {
          label: record.invoice_type,
          color: 'default',
        };
        return (
          <Space direction="vertical" size={2}>
            <Tag color={typeInfo.color} style={{ margin: 0 }}>
              {typeInfo.label}
            </Tag>
            <Text strong>{record.invoice_number}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Cliente',
      dataIndex: 'customer_name',
      key: 'customer_name',
      sorter: true,
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {DocumentTypes[record.customer_document_type]}:{' '}
            {record.customer_document_number}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Emisión / Pago',
      key: 'issue_date',
      sorter: true,
      render: (text, record) => {
        const isCredit = record.payment_condition === 'credit';
        const isPaid = record.payment_status === 'paid';

        const dueDate = isCredit
          ? moment(record.issue_date).add(30, 'days').format('DD/MM/YYYY')
          : null;

        const paymentDate = record.payment_date
          ? moment(record.payment_date).format('DD/MM/YYYY')
          : null;

        return (
          <Space direction="vertical" size={2}>
            <Text>{formatLocalDate(moment(record.issue_date))}</Text>

            {isCredit ? (
              isPaid ? (
                <Space size={4}>
                  <Tag color="orange" style={{ margin: 0 }}>
                    Crédito
                  </Tag>
                  <Tag color="green" style={{ margin: 0 }}>
                    Pagado
                  </Tag>
                  {paymentDate && (
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {paymentDate}
                    </Text>
                  )}
                </Space>
              ) : (
                <Space size={4}>
                  <Tag color="orange" style={{ margin: 0 }}>
                    Crédito
                  </Tag>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Vence: {dueDate}
                  </Text>
                </Space>
              )
            ) : (
              <Space size={4}>
                <Tag color="green" style={{ margin: 0 }}>
                  Contado
                </Tag>
                {paymentDate && (
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {paymentDate}
                  </Text>
                )}
              </Space>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
      align: 'right',
      render: (amount) => (
        <Text strong>
          {new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN',
          }).format(amount)}
        </Text>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status) =>
        status === 'cancelled' ? (
          <Tag color="red">Anulado</Tag>
        ) : (
          <Tag color="blue">Emitido</Tag>
        ),
    },
    {
      title: 'Acciones',
      dataIndex: 'id',
      key: 'edit',
      render: (id, record) => (
        <div className="flex" style={{ gap: 10 }}>
          {record.payment_condition === 'credit' &&
            record.payment_status !== 'paid' &&
            record.status !== 'cancelled' && (
              <Button
                style={{ padding: 0 }}
                type="link"
                onClick={() => openPaymentModal(record)}
                title="Registrar pago"
              >
                <IssuesCloseOutlined />
              </Button>
            )}
          <Button style={{ padding: 0 }} type="link" href={record.pdf_url}>
            <FilePdfOutlined />
          </Button>
          <Button
            style={{ padding: 0 }}
            type="link"
            onClick={() => printInvoice(record)}
          >
            <PrinterOutlined />
          </Button>
          <Button style={{ padding: 0 }} type="link" href={record.xml_url}>
            <FileExcelOutlined />
          </Button>

          <Button
            style={{ padding: 0 }}
            loading={isLoading}
            type="link"
            danger
            onClick={() => cancellInvoice(id)}
            disabled={record.status === 'cancelled'}
          >
            <DeleteOutlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="container">
      <InvoiceCreate
        open={open}
        onClose={() => setOpen(false)}
        onEmitted={(payload) => {
          setUrl((url) => url + '?nc=' + Math.random() * 100);
        }}
      />
      <Modal
        visible={paymentModalVisible}
        title="Registrar pago de factura al crédito"
        okText="Marcar como pagada"
        cancelText="Cancelar"
        onCancel={closePaymentModal}
        onOk={markAsPaid}
      >
        <Form layout="vertical" form={paymentForm}>
          <Form.Item label="Comprobante">
            <Input value={selectedBilling?.invoice_number} disabled />
          </Form.Item>

          <Form.Item label="Cliente">
            <Input value={selectedBilling?.customer_name} disabled />
          </Form.Item>

          <Form.Item
            name="payment_date"
            label="Fecha de pago"
            rules={[
              {
                required: true,
                message: 'Selecciona la fecha de pago.',
              },
            ]}
          >
            <DatePicker
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              inputReadOnly
              allowClear={false}
              disabledDate={(current) =>
                current && current > moment().endOf('day')
              }
            />
          </Form.Item>

          <Form.Item
            name="voucher"
            label="Número de operación"
            rules={[
              {
                required: true,
                message: 'Ingresa el número de operación.',
              },
            ]}
          >
            <Input placeholder="Ej: 01059564 BCP" />
          </Form.Item>
        </Form>
      </Modal>
      <CustomTable
        edit={currentId}
        title="Facturas"
        columns={columns}
        onCancel={() => setCurrentId(null)}
        url={url}
      >
        {({ filters, filterText, applyFilter, resetFilters }) => (
          <div className="flex">
            <Button type="dashed" onClick={() => setOpen(true)}>
              + Nueva Factura
            </Button>

            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                setFilterVisible(true);
              }}
            >
              Filtros
            </Button>

            <Drawer
              visible={filterVisible}
              onClose={() => setFilterVisible(false)}
              title="Filtrar facturas"
              width={350}
            >
              <Form layout="vertical" form={form}>
                <Form.Item name="company_id" label="Empresa">
                  <SelectAjax
                    onSelect={(value) => {
                      applyFilter({ company_id: value });
                    }}
                    size="middle"
                    showSearch
                    optionFilterProp="children"
                    url={'/companies-list'}
                    style={{ width: '100%' }}
                  />
                </Form.Item>

                <Form.Item name="customer_id" label="Cliente">
                  <SelectAjax
                    onSelect={(value) => {
                      applyFilter({ customer_id: value });
                    }}
                    size="middle"
                    showSearch
                    optionFilterProp="children"
                    url={'/customers-list'}
                    style={{ width: '100%' }}
                  />
                </Form.Item>

                <Form.Item
                  name="certification_type_id"
                  label="Tipo de certificación"
                >
                  <SelectAjax
                    onSelect={(value) => {
                      applyFilter({ certification_type_id: value });
                    }}
                    size="middle"
                    url={'/certification-types'}
                    style={{ width: '100%' }}
                  />
                </Form.Item>

                <Form.Item name="status" label="Estado comprobante">
                  <Select
                    placeholder="Selecciona"
                    allowClear
                    onChange={(value) => {
                      applyFilter({ status: value });
                    }}
                    style={{ width: '100%' }}
                  >
                    <Option value="invoiced">Emitido</Option>
                    <Option value="cancelled">Anulado</Option>
                  </Select>
                </Form.Item>

                <Form.Item name={'range'} label="Rango de fechas">
                  <RangePicker
                    value={selectedRange}
                    ranges={{
                      Hoy: [moment(), moment()],
                      Ayer: [
                        moment().add(-1, 'days').startOf('day'),
                        moment().add(-1, 'days').endOf('day'),
                      ],
                      'Este mes': [
                        moment().startOf('month'),
                        moment().endOf('month'),
                      ],
                      'El mes pasado': [
                        moment().add(-1, 'months').startOf('month'),
                        moment().add(-1, 'months').endOf('month'),
                      ],
                    }}
                    onChange={(values) => {
                      if (values) {
                        setSelectedRange(values);
                        applyFilter({
                          from: values[0].format('YYYY-MM-DD'),
                          to: values[1].format('YYYY-MM-DD'),
                        });
                      } else {
                        setSelectedRange(null);
                        applyFilter({
                          from: undefined,
                          to: undefined,
                        });
                      }
                    }}
                    style={{ width: '100%' }}
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    onClick={() => {
                      resetFilters();
                      setTimeout(() => {
                        form.resetFields();
                      }, 100);
                      setSelectedRange(null);
                    }}
                    icon={<ReloadOutlined />}
                    block
                  >
                    Resetear filtros
                  </Button>
                </Form.Item>
              </Form>
            </Drawer>

            <Button
              type="primary"
              ghost
              style={{ marginLeft: 8 }}
              onClick={() => exportInvoicesExcel({ ...filters, filterText })}
            >
              Exportar
            </Button>

            <Button
              type="primary"
              danger
              style={{ marginLeft: 8 }}
              onClick={() => exportInvoicesDbf(filters)}
            >
              Exportar DBF
            </Button>

            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              style={{ marginLeft: 8 }}
              onClick={() => exportInvoicesConcar(filters)}
            >
              Excel CONCAR
            </Button>
          </div>
        )}
      </CustomTable>
    </div>
  );
};

export default Invoices;
